import React, { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link, useHistory, useLocation } from 'react-router-dom'
import _ from 'lodash'
import { toast } from 'react-toastify'
import { TextField, Button, CircularProgress, Typography } from '@mui/material'

import { successLogin, changePassword } from 'redux/auth/actions'
import { login } from 'services/apiAuth'
import { isNetworkError } from 'utils/functions'
import { ROUTE_CONSTANTS } from 'utils/constants/routeConstants'

import { getOAuthURL, postFinishOAuthLogin } from 'services/apiKYE'
import { reportError } from 'utils/errorReporter'
import styles from './styles'
import { AuthStates, getUserTokenState, Scopes } from "../../utils/authUtils"

const Login = () => {
  const classes = styles()
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()

  const accessToken = useSelector((state) => state.auth.access_token)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [showUserAndPasswordForm, setShowUserAndPasswordForm] = useState(false)
  const [oAuthLoading, setOAuthLoading] = useState(false)

  // Cognito's authorization code is single-use; guard against re-firing the exchange (remounts/HMR/StrictMode).
  const oAuthCodeHandledRef = useRef(false)


  const handleOAuthLogin = () => {
    setOAuthLoading(true)
    getOAuthURL()
      .then((response) => {
        const { url } = response
        const returnPath = location?.state?.from
        const redirectUrl = returnPath
          ? `${url}:${window.btoa(returnPath)}`
          : url
        window.location.replace(redirectUrl)
      })
      .catch((error) => {
        toast.error('Houve um erro ao processar o login, tente novamente')
        reportError(error)
        setOAuthLoading(false)
      })
      .finally(() => {
      })
  }

  const handleSubmit = async (argEmail, argPassword) => {
    setLoading(true)

    try {
      const formData = new FormData()
      formData.append('username', argEmail)
      formData.append('password', argPassword)

      const res = await login(formData)
      if (res.status === 302) {
        dispatch(
          changePassword({
            email: argEmail,
            changePasswordSession: res.data.session,
          }),
        )

        return history.push('/password-change')
      }

      const userData = res.data

      if (userData) {
        dispatch(successLogin({ ...userData }))
      }

      // Redirect happens via the accessToken effect; if it never becomes
      // AUTHORIZED (e.g. missing scopes) we must stop spinning and warn the user.
      if (getUserTokenState(userData?.access_token, [Scopes.ACCESS_PANEL]) !== AuthStates.AUTHORIZED) {
        setLoading(false)
        toast.error('Não foi possível validar seu acesso. Contate o suporte.')
      }

      return false
    } catch (err) {
      setLoading(false)
      if (!isNetworkError(err)) {
        const messageError =
          _.get(err, 'response.data.msg_errors.0.msg') || 'Erro ao fazer login'
        toast.error(messageError)
      }

      return null
    }
  }

  const clearQueryAndHash = () => {
    history.replace(history.location.pathname, undefined)
  }

  const handleExternalLogin = (data, redirect_url = undefined) => {
    postFinishOAuthLogin(data)
      .then((userdata) => {
        if (userdata) {
          const infoLogin = { ...userdata }

          dispatch(successLogin(infoLogin))

          let redirectPathname = '/home'
          if (redirect_url) {
            redirectPathname = redirect_url
          }
          if (location?.state?.from) {
            redirectPathname = location.state.from
          }

          return history.push({
            pathname: redirectPathname,
          })
        }
        toast.error('Ocorreu um erro ao realizar o login')
        return false
      })
      .catch((error) => {
        reportError(error)
        if (error?.response?.status === 401) {
          toast.error(error.response.data?.detail || 'Credenciais inválidas')
          console.error(error)
        } else if (isNetworkError(error)) {
          toast.error('Erro ao se conectar com servidor')
        }
        else {
          toast.error('Ocorreu um erro ao realizar o login')
        }
      })
      .finally(() => {
        clearQueryAndHash()
        setOAuthLoading(false)
      })
  }


  const onEnterPress = (e) => {
    if (e.keyCode === 13) {
      e.preventDefault()
      return handleSubmit(email, password)
    }
    return false
  }

  useEffect(() => {
    if (getUserTokenState(accessToken, [Scopes.ACCESS_PANEL]) === AuthStates.AUTHORIZED) {
      history.push(ROUTE_CONSTANTS.HOME)
    }
  }, [accessToken])

    useEffect(() => {
    /*
     * DIFERENÇA DE FLUXOS NO COGNITO:
     * 1. PKCE (?code=): Usa Query String porque o código de autorização precisa ser enviado
     *    ao servidor/backend para ser trocado por tokens. O código sozinho é inútil sem o verifier.
     * 2. Implícito (#access_token=): Usa Hash porque o conteúdo após '#' NÃO é enviado ao servidor.
     *    Isso impede que tokens sensíveis fiquem expostos em logs de acesso ou cabeçalhos Referer.
     * Nota: PKCE é o padrão atual recomendado por segurança.
     */
    const search =  new URLSearchParams(location?.search)
    if (search?.get('code') && !oAuthCodeHandledRef.current) {
      setOAuthLoading(true)
      oAuthCodeHandledRef.current = true
      const [originalState, redirect_url] = search?.get('state')?.split(':') ?? []
      const data = { ...Object.fromEntries(search.entries()), state: originalState }

      handleExternalLogin(data, redirect_url ? window.atob(redirect_url) : undefined)
    }
    // if (hashData && hashData.access_token) {
    //   const [originalState, redirect_url] = hashData.state?.split(':') ?? []
    //   const data = { ...hashData, state: originalState }

    //   handleExternalLogin(data, redirect_url ? window.atob(redirect_url) : undefined)
    // }
  }, [location?.search])
  
  return (
    <div className={classes.root}>
      <div className={classes.sideTriangle} />
      <aside className={classes.aside}>
        <h1>
          <img
            src="assets/images/logos/client_logo.png"
            alt="Logo"
            className={classes.logo}
          />
        </h1>
        <div style={{ marginBottom: 50 }}>
          <h1 style={{ fontSize: 50 }}>Acesso</h1>
          <p style={{ fontSize: 18 }}>
            Digite seus dados de acesso à plataforma
          </p>
        </div>
      </aside>
      <main className={classes.main}>
        <h1 className={classes.title}>Know Your Employee</h1>
        <div className={classes.inputsContainer}>
          {showUserAndPasswordForm ? (
            <form className={classes.formContainer}>
              <TextField
                variant="standard"
                id="email"
                label="E-mail"
                className={classes.textField}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputLabelProps={{ shrink: true }}
                type="email"
                required={true}
                autoCorrect="off"
                autoCapitalize="off" />

              <TextField
                variant="standard"
                id="password"
                label="Senha"
                className={classes.textField}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={onEnterPress}
                InputLabelProps={{ shrink: true }}
                autoComplete="off"
                type="password"
                required={true}
                autoCorrect="off"
                autoCapitalize="off" />
            </form>
          ) : (
            <Button
              variant="contained"
              className={classes.microsoftButton}
              onClick={() => handleOAuthLogin()}
              startIcon={oAuthLoading ? <CircularProgress className={classes.progress} /> : <img src="assets/images/logos/microsoft.svg" alt="" />}
            >
              Entrar com Microsoft
            </Button>
          )}
          
          {showUserAndPasswordForm && (
            <Typography className={classes.link}>
              <Link to={ROUTE_CONSTANTS.FORGOT_PASSWORD}>Esqueci a senha</Link>
            </Typography>
          )}
        </div>

        <div className={classes.buttonContainer}>
          {showUserAndPasswordForm && (
            <Button
              color="primary"
              variant="contained"
              className={classes.button}
              onClick={() => handleSubmit(email, password)}
              disabled={!!loading}
            >
              {loading ? (
                <CircularProgress className={classes.progress} />
              ) : (
                'Acessar'
              )}
            </Button>
          )}

            <Button
              variant="text"
              className={classes.alternativeButton}
              onClick={() => setShowUserAndPasswordForm(!showUserAndPasswordForm)}
            >
              Tentar de outra forma
            </Button>
        </div>
      </main>
    </div>
  )
}

export default Login
